import { Box, Checkbox, Flex, Link, Text } from "@chakra-ui/react";
import { useContext } from "react";
import { useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import Map, {
  Layer,
  MapGeoJSONFeature,
  MapLayerMouseEvent,
  MapRef,
  NavigationControl,
  Popup,
  Source,
} from "react-map-gl/maplibre";

import { AuthMode, FilterSet, useFrontendSettings } from "@nens/ldo/api-v1";
import { LdoAuthContext } from "@nens/ldo/auth-v1";
import { convertLngLatBoundsToExtent, useMaptilerStyleJson } from "@nens/react-map-gl/utils";
import { Extent } from "@nens/shared-utils";

interface MapFilterProps {
  authMode: AuthMode;
  filterSet: FilterSet;
  checkboxes: Record<number, true>;
  setBBox: (extent: Extent) => void;
  updateWhileZooming: boolean;
  setUpdateWhileZooming: (updateWhileZooming: boolean) => void;
}

const MIN_ZOOM = 5;
const MAX_ZOOM = 21;

export function MapFilter({
  authMode,
  filterSet,
  setBBox,
  checkboxes,
  updateWhileZooming,
  setUpdateWhileZooming,
}: MapFilterProps) {
  const [popup, setPopup] = useState<null | {
    latitude: number;
    longitude: number;
    features: MapGeoJSONFeature[];
  }>(null);
  const frontendSettings = useFrontendSettings();
  const mapRef = useRef<MapRef>(null);

  const handleClick = (e: MapLayerMouseEvent) => {
    const { lng, lat } = e.lngLat;
    const features = mapRef.current?.queryRenderedFeatures(e.point, { layers: ["scenarios"] });
    if (features) {
      // Otherwise, show a list of scenarios at that position
      if (!popup) {
        mapRef.current?.flyTo({ center: [lng, lat] });
      }
      setPopup({ latitude: lat, longitude: lng, features });
    }
  };

  const style = useMaptilerStyleJson(
    "d481b712-934d-485d-bfdb-bc1abf01fd5c",
    frontendSettings.isSuccess ? frontendSettings.data.maptiler_api_key : null,
    false
  );

  const { mapLibreTransformFunction, tenantId } = useContext(LdoAuthContext);

  const checkboxIds = Object.keys(checkboxes).map((id) => Number.parseInt(id));

  if (!style) return null;

  const filters = filterSet.toMaplibreFilter();

  return (
    <Box w="full" h="400px" flexShrink="0" bgColor="white" p="16px" rounded="lg">
      <Map
        style={{
          borderRadius: 6,
        }}
        ref={mapRef}
        mapStyle={style}
        id={`filter-map-${tenantId}`}
        attributionControl={false}
        scrollZoom
        interactiveLayerIds={["scenarios"]}
        onMoveEnd={(evt) => setBBox(convertLngLatBoundsToExtent(evt.target.getBounds()))}
        onClick={handleClick}
        transformRequest={mapLibreTransformFunction}
        initialViewState={{
          longitude: 5.4612108,
          latitude: 52,
          zoom: 5.5,
        }}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
      >
        <NavigationControl position="top-right" />
        <Source
          tiles={[
            /* tenantId parameter does nothing but prevents caching problems */
            `${window.location.protocol}//${window.location.host}/api/v1-alpha/vector-tile/{z}/{x}/{y}?mode=${authMode}&tenant=${tenantId}`,
          ]}
          id="scenarios"
          type="vector"
        >
          <Layer
            id="scenarios"
            minzoom={MIN_ZOOM - 1}
            maxzoom={MAX_ZOOM + 1}
            type="circle"
            source-layer="scenario"
            filter={filters}
            paint={{
              "circle-color": [
                "match",
                ["get", "status"],
                "incomplete",
                "#FEEBC8",
                "files_present",
                "#FEFCBF",
                "complete",
                "#C6F6D5",
                "import_checked",
                "#E9D8FD",
                "quality_control_requested",
                "#FED7E2",
                "quality_checked",
                "#B2F5EA",
                "quality_rejected",
                "#FED7D7",
                "archived",
                "#BEE3F8",
                "#CE614A",
              ],
              "circle-radius": 5,
              "circle-stroke-width": ["case", ["in", ["id"], ["literal", checkboxIds]], 4, 2],
              "circle-stroke-color": [
                "case",
                ["in", ["id"], ["literal", checkboxIds]],
                "#cc604d" /* bij12_1.700 */,
                "#858585",
              ],
            }}
          />
        </Source>
        {popup ? (
          <Popup
            anchor="left"
            longitude={Number(popup.longitude)}
            latitude={Number(popup.latitude)}
            onClose={() => setPopup(null)}
            style={{
              maxHeight: "250px",
            }}
          >
            <Text color="#333" fontWeight="semibold" fontSize="16px">
              {popup.features?.length}{" "}
              {popup.features?.length && popup.features?.length === 1 ? (
                <FormattedMessage id="map.scenario" defaultMessage="scenario" />
              ) : (
                <FormattedMessage id="map.scenarios" defaultMessage="scenarios" />
              )}
            </Text>
            <Box overflowY="scroll" overflowX="hidden" maxHeight="300px" marginY={4}>
              {popup.features?.map((feature) => {
                return (
                  <div
                    key={feature.id}
                    style={{
                      color: "black",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "300px",
                      overflowX: "hidden",
                      fontSize: "1rem",
                    }}
                  >
                    <Link href={`/scenarios/${feature?.id}`} title={feature?.properties?.name}>
                      {feature?.id} - {feature?.properties?.name}
                    </Link>
                  </div>
                );
              })}
            </Box>
            {popup.features?.length > 1 ? (
              <Link
                color="black"
                href={`/scenarios/compare/?ids=${popup.features
                  .map((feature) => feature.id)
                  .join(",")}`}
                title={"Compare"}
              >
                <FormattedMessage
                  id="pages.scenario.map.comparelink"
                  defaultMessage="Compare these scenarios"
                />
              </Link>
            ) : null}
          </Popup>
        ) : null}
      </Map>
      <Flex
        p="16px"
        position="absolute"
        w="full"
        bg="red"
        bottom="0"
        justifyContent="center"
        alignItems="center"
        bgColor="transparent"
        pointerEvents="none"
      >
        <Flex
          bgColor="white"
          h="50px"
          color="black"
          px="20px"
          my="10px"
          rounded="md"
          pointerEvents="auto"
        >
          <Checkbox
            isChecked={updateWhileZooming}
            onChange={() => setUpdateWhileZooming(!updateWhileZooming)}
          >
            <FormattedMessage
              id="map.update_while_zooming"
              defaultMessage="Update results while zooming map"
            />
          </Checkbox>
        </Flex>
      </Flex>
    </Box>
  );
}
